import { useState, useMemo, useEffect } from "react"
import { Dialog, DialogContent, DialogTrigger } from "../../../../components/ui/dialog"
import { ExploreWidgetConfig } from "../../config"
import { clampParty } from "../../../../common/occupancy"
import { useExplorer, PETS_MAX } from "../../providers"
import { useExplorerCss } from "../../hooks/useExplorerCss"
import { useWidget } from "../../../../providers/widget"
import NumberControl from "./NumberControl"
import FieldClear from "./pro/FieldClear"
import { useSbpLabels } from "./pro/labels"
import { useSbpCompact, SrLabel } from "./pro/compact"
import { useSbpPopover } from "./pro/useSbpPopover"

// `pro` renders the Searchbar Pro trigger (label + value lines, #853); dialog
// content and apply semantics are identical in both variants.
// `popover` (desktop pro bar instances only) anchors the open dialog to the
// bar's right edge as a scrimless popover — toolkit guests-popover shape.
// `sheet` (mobile staged sheet, Eli r5) renders NO dialog at all: a stage
// header + the guest rows expanded INLINE in the sheet's accordion, with
// LIVE-apply semantics — the module's sheet Who section has no Apply button
// (its steppers write straight into the pending search), so staging here
// would strand edits that the sheet's Search button then ignored.
export default function GuestFilter({ pro = false, popover = false, sheet = false, sheetActive = false, onSheetActivate, advanceSignal = 0, onApplyAdvance }: { pro?: boolean; popover?: boolean; sheet?: boolean; sheetActive?: boolean; onSheetActivate?: () => void; advanceSignal?: number; onApplyAdvance?: () => void } = {}) {
  const { options } = useWidget<ExploreWidgetConfig>()
  const { state, setFilter, setPetsCount } = useExplorer()
  const cssString = useExplorerCss()
  const [open, setOpen] = useState(false)
  const labels = useSbpLabels()
  const compactBar = useSbpCompact()
  const { triggerRef, measureNow, anchored, wrapperStyle } = useSbpPopover(popover, 'right', open)

  // Chained popovers (#923): the bar coordinator bumps this to hand the hero
  // bar to this field (completed date range → WHO).
  useEffect(() => {
    if (!advanceSignal) return
    measureNow()
    setOpen(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advanceSignal])
  const [adults, setAdults] = useState(state.visitorFilters.adults ?? 1)
  const [children, setChildren] = useState(state.visitorFilters.children ?? 0)
  // Stepper count (#932, module BOUNDS.pets 0-3). The applied filter stays the
  // boolean pets=true — the module's own collapse (searchbar-pro.js:2836,
  // pets=yes|no NOT the count) — so the draft seeds from the shared
  // presentation count, which the provider keeps consistent with the boolean.
  const [pets, setPets] = useState(state.petsCount)
  // Pets row is admin-toggleable (#558, WP: explorer_extra_fields → pets).
  // When hidden, pets never enter the applied filters either.
  const showPets = options.showPetsFilter ?? true

  // Sheet stages have no open/close dialog cycle — the accordion's active
  // flag is their "open" for every open-keyed behavior below.
  const effectiveOpen = sheet ? sheetActive : open

  // Re-seed the draft from the APPLIED filters on every open/close flip: the
  // pro variant mounts desktop and mobile instances side by side (#853), so an
  // apply in one would otherwise leave the other's mount-time draft stale —
  // and the legacy staged modal's trigger renders the DRAFT, so a dismissal
  // without Apply must discard the abandoned edits or the closed trigger
  // advertises values that were never applied (#932 codex r1).
  useEffect(() => {
    setAdults(state.visitorFilters.adults ?? 1)
    setChildren(state.visitorFilters.children ?? 0)
    setPets(state.petsCount)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveOpen])

  const displayValue = useMemo(() => {
    const parts: string[] = []
    const total = adults + children
    if (total > 0) parts.push(`${total} guest${total !== 1 ? 's' : ''}`)
    // Module label shape: pets never fold into the guests total — they join as
    // their own pluralized segment, "3 guests, 1 pet" (buildGuestLabel,
    // searchbar-pro.js:2811-2823; localized pet/pets since v3.166.0).
    if (showPets && pets > 0) parts.push(`${pets} pet${pets !== 1 ? 's' : ''}`)
    return parts.join(', ') || '1'
  }, [adults, children, pets, showPets])

  // Unbounded again (#850 product call 1, decided 2026-09-01): the explorer
  // searches ACROSS properties, so there's no single per-property maximum to
  // enforce client-side — GUEST_MAX_FALLBACK is a DISPLAY-ONLY loading bound
  // for the booking picker (common/occupancy.ts), not a search ceiling, and
  // must not gate what a visitor can even express here. `clampParty` is still
  // reused for its sanity floor (>=1 adult, non-negative integers) with an
  // unbounded ceiling — Infinity is "no cap", not a new arbitrary number.
  const commitParty = (a: number, c: number, p: number) => {
    const party = clampParty({ adults: a, children: c }, { maxGuests: Infinity })
    // Count first, boolean second: SET_FILTER re-anchors petsCount off the
    // boolean, so the count must already be in place when it lands.
    setPetsCount(showPets ? p : 0)
    setFilter({
      adults: party.adults > 0 ? party.adults : undefined,
      children: party.children > 0 ? party.children : undefined,
      pets: showPets && p > 0 ? true : undefined,
    })
  }

  // Sheet AND desktop popover commit on every tap (module live-apply, r7
  // #936): the module's steppers write guestState and sync the trigger label
  // in the same synchronous call, popover still open
  // (searchbar-pro.js:2735-2754) — no staged draft exists there at all. Only
  // the legacy centred modal still stages until Apply.
  const changeAdults = (v: number) => { setAdults(v); if (sheet || popover) commitParty(v, children, pets) }
  const changeChildren = (v: number) => { setChildren(v); if (sheet || popover) commitParty(adults, v, pets) }
  const changePets = (v: number) => { setPets(v); if (sheet || popover) commitParty(adults, children, v) }

  const handleApply = () => {
    // On the popover this is the module's "Done": values are already
    // committed per tap, so the commit here is an idempotent no-op kept for
    // the legacy modal, where it IS the commit point.
    commitParty(adults, children, pets)
    setOpen(false)
    // Chain hand-off (#923): the module's guests Done hands the bar to the
    // next field in HERO mode only (searchbar-pro.js:2831-2833, gated
    // !guestsRefineBar); on the inline explorer advanceFrom no-ops (r7 #936)
    // and this just closes, like the module's refine bar.
    if (popover) onApplyAdvance?.()
  }

  const handleClear = () => {
    setAdults(1)
    setChildren(0)
    setPets(0)
    setFilter({ adults: undefined, children: undefined, pets: undefined })
  }

  // Pro trigger reads the APPLIED filters, not the dialog's local state (which
  // seeds adults=1 before anything is applied) — "Add guests" until a real
  // apply, and the label text itself derives from applied values too: with the
  // pro variant's dual desktop/mobile mounts, a sibling instance's apply would
  // otherwise leave this trigger's label stale until the dialog is opened.
  const guestsApplied = state.visitorFilters.adults !== undefined
    || state.visitorFilters.children !== undefined
    || state.visitorFilters.pets !== undefined
  const appliedDisplayValue = useMemo(() => {
    const vf = state.visitorFilters
    const total = (vf.adults ?? 0) + (vf.children ?? 0)
    const parts: string[] = []
    if (total > 0) parts.push(`${total} guest${total !== 1 ? 's' : ''}`)
    // petsCount is floored at 1 whenever the boolean is on (provider
    // invariant), so a boolean-only deep link reads "1 pet".
    if (showPets && vf.pets) parts.push(`${state.petsCount} pet${state.petsCount !== 1 ? 's' : ''}`)
    return parts.join(', ') || '1'
  }, [state.visitorFilters, state.petsCount, showPets])
  // Shared by the dialog body and the sheet stage body: same rows, same
  // markup — only the commit timing differs (the changeX handlers above).
  const guestRows = (
    <>
      {/* Adults */}
      <div className="hk-flex hk-flex-middle">
        <div className="hk-width-3-5">
          <label className="hk-form-label">Adults</label>
          <div className="hk-text-meta">Age 13 and above</div>
        </div>
        <div className="hk-width-2-5">
          <div className="hk-form-controls">
            {/* No max: unbounded (#850 product call 1) — NumberControl counts up
                without a bound when max is left off, same as bedrooms/bathrooms. */}
            <NumberControl name="adults" value={adults} min={1} onChange={changeAdults} label="Adults" />
          </div>
        </div>
      </div>

      {/* Children */}
      {/* Row rhythm rides .hfse-guest-row-spacing (was an inline
          marginTop, which the pro popover restyle (#931) could not
          zero without !important). */}
      <div className="hk-flex hk-flex-middle hk-margin hfse-guest-row-spacing">
        <div className="hk-width-3-5">
          <label className="hk-form-label">Children</label>
          <div className="hk-text-meta">Age 2-12</div>
        </div>
        <div className="hk-width-2-5">
          <div className="hk-form-controls">
            {/* No max: unbounded (#850 product call 1) — see the adults control above. */}
            <NumberControl name="children" value={children} min={0} onChange={changeChildren} label="Children" />
          </div>
        </div>
      </div>

      {/* Pets — a stepper like the module's guest picker (#932): the module
          renders pets with the same generic stepper row as Adults/Children
          (GuestPicker/Renderer.php render_row, 32px round buttons), bounds
          0-3 (searchbar-pro.js:171 BOUNDS.pets). The count is presentation
          only; commitParty collapses it to the boolean filter. */}
      {showPets && (
      <div className="hk-flex hk-flex-middle hk-margin hfse-guest-row-spacing">
        <div className="hk-width-3-5">
          <label className="hk-form-label">Pets</label>
          <div className="hk-text-meta">Bringing a pet?</div>
        </div>
        <div className="hk-width-2-5">
          <div className="hk-form-controls">
            <NumberControl name={`pets${sheet ? '-sheet' : ''}`} value={pets} min={0} max={PETS_MAX} onChange={changePets} label="Pets" />
          </div>
        </div>
      </div>
      )}
    </>
  )

  const renderContent = () => (
    <DialogContent
      className={`hfse-form-modal hfse-form-guests-options${pro ? ' hfse-sbp-popover hfse-sbp-guests-dress' : ''}${anchored ? ' hfse-sbp-popover-anchored' : ''}`}
      overlayClassName={pro ? `hfse-sbp-popover-overlay${anchored ? ' hfse-sbp-popover-overlay-anchored' : ''}` : undefined}
      wrapperStyle={wrapperStyle}
      elevateDom
      cssString={cssString}
      title="Guests"
      hideClose={anchored}
      // Pointer/Escape dismiss only — chained hand-offs must survive the
      // closing dialog's focus return (#923; see DateFilter).
      onFocusOutside={popover ? (e) => e.preventDefault() : undefined}
      // Module parity (#931): nothing is pre-focused in the reference guest
      // picker — Radix otherwise lands focus on the readonly Adults count
      // and SELECTS its text, painting a stray ring + selection highlight
      // (same class of artifact as the slice D mobile-sheet autofocus).
      onOpenAutoFocus={pro ? (e) => e.preventDefault() : undefined}
    >
            {/* Anchored popovers are headerless like the toolkit's. */}
            {!anchored && (
              <div className="hk-modal-header">
                <span className="hk-h4">
                  Guests
                </span>
              </div>
            )}

            <div className="hk-modal-body hk-flex-1">{guestRows}</div>

            <div className="hk-modal-footer hk-flex">
              {(adults !== 1 || children !== 0 || (showPets && pets > 0)) && (
                <button type="button" className="hk-button hk-button-link hfse-modal-reset-button" onClick={handleClear}>
                  Clear
                </button>
              )}
              <button
                type="button"
                className="hk-button hk-button-small bg-accent text-accent-foreground hk-margin-auto-left hfse-modal-next-button"
                onClick={handleApply}
                style={{ marginLeft: 'auto' }}
              >
                {/* Popover commits live (r7 #936), so its button is the
                    module's close-only "Done" (template close-guests,
                    search-bar.php:671); the legacy modal's Apply still
                    commits. */}
                {popover ? 'Done' : 'Apply'}
              </button>
            </div>
          </DialogContent>
  )

  if (sheet) {
    return (
      <>
        <button
          type="button"
          className={`hfse-sbp-field hfse-sbp-field-btn hfse-sbp-guests-field hfse-sbp-stage-header${guestsApplied ? ' hfse-sbp-field-has-value' : ''}`}
          aria-expanded={sheetActive}
          onClick={onSheetActivate}
        >
          <span className="hfse-sbp-field-label">{labels.whoLabel}</span>
          <span className={`hfse-sbp-field-value ${guestsApplied ? '' : 'hfse-sbp-field-placeholder'}`}>
            {guestsApplied ? appliedDisplayValue : labels.whoIdle}
          </span>
          {guestsApplied && <FieldClear label="Clear guests" onClear={handleClear} />}
        </button>
        {sheetActive && (
          // hfse-form-guests-options + the dress class pull the #931 module
          // guest-picker treatment onto the inline rows; hk-modal-body keeps
          // the row-rule selectors (`.hk-modal-body > .hk-flex`) matching.
          <div className="hfse-sbp-stage-body hfse-form-guests-options hfse-sbp-guests-dress">
            <h3 className="hfse-sbp-stage-question">Who&#39;s coming?</h3>
            <div className="hk-modal-body hk-flex-1">{guestRows}</div>
          </div>
        )}
      </>
    )
  }

  if (pro) {
    return (
      // Measuring inside the open flip batches the anchor rect with the open
      // state — the portal's first paint is already positioned.
      // Non-modal for desktop popovers: one-click field switching (#923) —
      // see the DateFilter pro branch for the full rationale.
      <Dialog modal={!popover} open={open} onOpenChange={(o) => { if (o) measureNow(); setOpen(o) }}>
        <DialogTrigger asChild>
          <button
            type="button"
            ref={triggerRef}
            className={`hfse-sbp-field hfse-sbp-field-btn hfse-sbp-guests-field${guestsApplied ? ' hfse-sbp-field-has-value' : ''}`}
          >
            {compactBar.compact ? <SrLabel>{labels.whoLabel}</SrLabel> : <span className="hfse-sbp-field-label">{labels.whoLabel}</span>}
            <span className={`hfse-sbp-field-value ${guestsApplied ? '' : 'hfse-sbp-field-placeholder'}`}>
              {guestsApplied ? appliedDisplayValue : (compactBar.compact ? compactBar.placeholderWho : labels.whoIdle)}
            </span>
            {/* handleClear resets the draft AND calls setFilter, so the pill
                clear commits (toolkit clearField parity). */}
            {guestsApplied && <FieldClear label="Clear guests" onClear={handleClear} />}
          </button>
        </DialogTrigger>
        {renderContent()}
      </Dialog>
    )
  }

  return (
    <div className="hfse-form-guests-field-container">
      <div className="field-control hfse-form-guests-field-control">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <div className="hfse-form-field-trigger">
              <span className="hfse-form-field-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </span>
              <input
                type="text"
                className="hk-input hfse-form-guests-value"
                placeholder="1"
                value={displayValue}
                readOnly
              />
            </div>
          </DialogTrigger>
          {renderContent()}
        </Dialog>
      </div>
    </div>
  )
}
